import type { DefaultTheme } from 'styled-components';

/* Utils */
import { i18n } from 'utils/i18n';

/* Styled Components */
import { SectionTitle } from 'styles/landing/LandingPage';

/* Assets */
import ShipToStoreImage from 'assets/ShipToStore.png';
import UpsellProductsImage from 'assets/UpsellProducts.png';
import LocalAppImage from 'assets/LocalApp.png';
import FulfillPickupImage from 'assets/FulfillPickup.png';
import TestimonialImage1 from 'assets/StoreOwner1.png';
import TestimonialImage2 from 'assets/StoreOwner2.png';
import TestimonialImage3 from 'assets/StoreOwner3.png';

export const FACEBOOK_LINK = 'https://www.facebook.com/wishlocalapp';
export const INSTAGRAM_LINK = 'https://www.instagram.com/wishlocalapp/';
export const TWITTER_LINK = 'https://twitter.com/wishlocalapp';
export const YOUTUBE_LINK = 'https://www.youtube.com/c/wishlocal';
export const BLOG_LINK = 'https://blog.local.wish.com/';

export const COMPANY_INFO_LINK = 'https://www.wish.com/companyinfo';
export const CAREERS_LINK = 'https://www.wish.com/careers';
export const SELL_ON_WISH_LINK = 'https://merchant.wish.com/welcome';
export const INTERLECTUAL_PROPERTY_LINK =
  'https://merchant.wish.com/intellectual-property';

export const HOW_IT_WORKS_DATA = () => [
  {
    imageSrc: ShipToStoreImage,
    subTitle: i18n('Customers shop on Wish and ship items to your store'),
    bodyText: i18n(
      "We'll list your store as a pickup destination for popular products on Wish, which means more foot traffic, more sales, and new regular customers for your business!",
    ),
  },
  {
    imageSrc: UpsellProductsImage,
    subTitle: i18n('When customers arrive, upsell your products and services'),
    bodyText: i18n(
      'Making your store a Wish pickup location creates an opportunity for you to get to know shoppers in your area. Participating stores say more than 1 in 4 pickups lead to a new sale.',
    ),
  },
  {
    imageSrc: LocalAppImage,
    subTitle: i18n(
      'All your day-to-day Wish Local operations at your fingertips',
    ),
    bodyText: i18n(
      'The Wish Local app makes managing Wish inventory simple. View and check in orders, track your performance, and get help when you need it—all in the app.',
    ),
  },
  {
    imageSrc: FulfillPickupImage,
    subTitle: i18n('Earn a commission for every fulfilled pickup order'),
    bodyText: i18n(
      'To receive your commission, simply confirm pickup within the Wish Local app. Payments are issued biweekly via PayPal.',
    ),
  },
];

export const TESTIMONIAL_DATA = () => [
  {
    title: i18n('Dania increased her clientele by becoming a pickup location'),
    storeName: "Dania's Hair Studio",
    location: i18n('Florida, United States'),
    testimonial: i18n(
      "If you're a store that's in a corner like me, and you're struggling to create new clients, Wish Local is perfect.",
    ),
    imageSrc: TestimonialImage1,
  },
  {
    title: i18n("Bigabyte's store owner gained new regulars"),
    storeName: 'Bigabyte',
    location: i18n('Birkenhead, United Kingdom'),
    testimonial: i18n(
      'We have around 20 or 30 customers a day who come from Wish. And of those, we found that a large number became regular customers of the shop itself.',
    ),
    imageSrc: TestimonialImage2,
  },
  {
    title: i18n("Edicola Mocama's business grew during the pandemic"),
    storeName: 'Edicola Mocama',
    location: i18n('Rome, Italy'),
    testimonial: i18n(
      'During the day, about 20 people come in to pick up their Wish parcels, and half of them, buy my products as well.',
    ),
    imageSrc: TestimonialImage3,
  },
];

export const ABOUT_WISH_DATA = (theme: DefaultTheme) => [
  i18n(
    '{%1=number of monthly wish shoppers} Monthly Average Users ',
    <SectionTitle color={theme.white}>
      {i18n(
        '{%1=M stands for millions. Use: 44M+Monthly Average Users }M+',
        44,
      )}
    </SectionTitle>,
  ),
  i18n(
    '{%1=number of wish local stores} Wish Local stores worldwide',
    <SectionTitle color={theme.white}>
      {i18n(
        '{%1=K stands for thousands. Use: 60K+ Wish Local stores worldwide}K+',
        60,
      )}
    </SectionTitle>,
  ),
  i18n(
    '{%1=number items are sold per day} Items are sold per day',
    <SectionTitle color={theme.white}>
      <span>&#8764;</span>900,000
    </SectionTitle>,
  ),
];

export const FAQS = () => [
  {
    question: i18n('How can Wish Local help my business?'),
    answer: `${i18n(
      "By making your store a pickup location for Wish orders, you'll gain access to the customer base of one of the world’s most downloaded shopping apps.",
    )}* ${i18n(
      "You'll experience an increase in foot traffic, get to know more shoppers in your local area, and create an opportunity to upsell your products and services. Plus, you'll earn a commission on every eligible pickup you help fulfill.",
    )}`,
    footnote: i18n(
      'from 2017-2019. Source: SensorTower',
      'Footnote to explain the sentence "Wish is the #1 most downloaded shopping app"',
    ),
  },
  {
    question: i18n('How does the pickup program work?'),
    answer: i18n(
      "When customers shop on Wish, they can choose to send their package to a participating Wish Local store to save on shipping. We'll regularly bundle these orders and send them to your store for you to hold until customers pick them up. As you receive packages, you'll check them in to notify customers, and you'll fulfill orders, all within the Wish Local app. We've designed the app to make checking in packages, keeping track of pickups, and managing your Wish inventory quick and straightforward.",
    ),
  },
  {
    question: i18n('How do I get paid?'),
    answer: i18n(
      "We'll pay you a small commission for each eligible Wish order you help fulfill. With the Wish Local app, you can also monitor your performance and track your earnings along the way. Payments are issued biweekly via PayPal. If you don't have a PayPal account, you can create one on their website for free. Payments are subject to a PayPal transaction fee.",
    ),
  },
  {
    question: i18n('What are the requirements to join?'),
    answer: i18n(
      "Any business with a physical storefront where employees can interact with Wish shoppers in-person can enroll in Wish Local for free. Once your store is reviewed and approved, you'll also need a smartphone (iOS or Android) so you can download the Wish Local app and process orders.",
    ),
  },
  {
    question: i18n('How much space do I need?'),
    answer: i18n(
      'Most store owners use a small space with a few shelves in a backroom or office to store Wish packages. Customers will have up to 2 weeks to pick up their order. For a smoother pickup experience, we recommend sorting orders alphabetically by the code on the package.',
    ),
  },
];
